import type { ColumnDef, Row } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type {
  Campaigns as CampaignsCollection,
  Flights as FlightsCollection,
  Goal,
  Segment,
} from '@feathr/blackbox';
import { TanStackHeader, TanStackTable } from '@feathr/components';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';

interface IProps {
  segment: Segment;
}

interface IUsageColumnsProps {
  Campaigns: CampaignsCollection;
  Flights: FlightsCollection;
  localUrl: (url: string) => string;
  t: TFunction;
}

function conversionPixelUsageColumns({
  Campaigns,
  Flights,
  localUrl,
  t,
}: IUsageColumnsProps): Array<ColumnDef<Goal>> {
  return [
    {
      id: 'name',
      header: (): JSX.Element => <TanStackHeader title={t('Name')} />,
      enableSorting: false,
      meta: { isFlex: true, isPrimary: true },
      cell({ row }): JSX.Element {
        const goal = row.original;
        return (
          <Observer>
            {(): JSX.Element => {
              const campaign =
                goal.get('kind') === 'campaign' ? Campaigns.get(goal.get('parent')) : undefined;
              if (campaign && !campaign.isPending) {
                return <Link to={localUrl(campaign.getItemUrl())}>{campaign.name}</Link>;
              }
              const flight =
                goal.get('kind') === 'flight' ? Flights.get(goal.get('parent')) : undefined;
              if (flight && !flight.isPending) {
                return <Link to={localUrl(flight.getItemUrl())}>{flight.get('name')}</Link>;
              }
              return <>-</>;
            }}
          </Observer>
        );
      },
    },
    {
      id: 'kind',
      accessorFn: (row): string => row.get('kind') ?? '',
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      size: 150,
      cell({ row }): JSX.Element {
        return <Observer>{(): JSX.Element => <>{row.original.get('kind')}</>}</Observer>;
      },
    },
  ];
}

function ConversionPixelUsageTable({ segment }: Readonly<IProps>): JSX.Element {
  const { Campaigns, Flights, Goals } = useStore();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();

  const filters = useMemo(() => ({ is_archived__ne: true, segment: segment.id }), [segment.id]);

  // initialPageSize 20 matches the legacy Table default; the hook's own default is 10.
  const tableProps = useCollectionTable<Goal>(Goals, {
    defaultParams: { filters },
    initialPageSize: 20,
  });

  const columns = useMemo(
    () => conversionPixelUsageColumns({ Campaigns, Flights, localUrl, t }),
    [Campaigns, Flights, localUrl, t],
  );

  return (
    <TanStackTable<Goal>
      {...tableProps}
      columns={columns}
      noDataText={t('This conversion pixel is not used in any campaigns or flights.')}
    />
  );
}

const ObservedConversionPixelUsageTable = observer(ConversionPixelUsageTable);

/**
 * Adapts the usage table to `TanStackTable`'s `renderSubComponent`, which hands the whole row
 * rather than the model.
 */
export function renderConversionPixelUsage(row: Row<Segment>): JSX.Element {
  return <ObservedConversionPixelUsageTable segment={row.original} />;
}
